import DatePeriodSelector, {
  type DatePeriodValue,
} from '@/modules/analytics/components/date-period-selector';
import OrdersOverTimeCard from '@/modules/analytics/components/orders-over-time-card';
import PopularSearchesCard from '@/modules/analytics/components/popular-searches-card';
import VisitsOverTimeCard from '@/modules/analytics/components/visits-over-time-card';
import ZeroResultSearchesCard from '@/modules/analytics/components/zero-result-searches-card';
import periodPresetToMatomoDate from '@/modules/analytics/functions/periodPresetToMatomoDate';
import useOrdersOverTimeQuery from '@/modules/analytics/queries/useOrdersOverTimeQuery';
import usePopularSearchesQuery from '@/modules/analytics/queries/usePopularSearchesQuery';
import useVisitsOverTimeQuery from '@/modules/analytics/queries/useVisitsOverTimeQuery';
import useZeroResultSearchesQuery from '@/modules/analytics/queries/useZeroResultSearchesQuery';
import config from '@/modules/config/functions/config';
import { Helmet } from '@dr.pogodin/react-helmet';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { GenericHeader } from '@thisisdmg/tirecloud-patterns/v2';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './index.module.css';

function AnalyticsPage() {
  const { t } = useTranslation();
  const [datePeriod, setDatePeriod] = useState<DatePeriodValue>({
    mode: 'last30',
    matomoParams: periodPresetToMatomoDate('last30'),
  });
  const { period, date } = datePeriod.matomoParams;

  const visitsQuery = useVisitsOverTimeQuery({ period, date });
  const ordersQuery = useOrdersOverTimeQuery({ period, date });
  const zeroResultSearchesQuery = useZeroResultSearchesQuery({ period, date });
  const popularSearchesQuery = usePopularSearchesQuery({ period, date });

  return (
    <>
      <Helmet>
        <title>{t('analytics.title')}</title>
      </Helmet>
      <div className={classes.container}>
        <GenericHeader
          title={t('analytics.title')}
          primaryAction={
            <DatePeriodSelector value={datePeriod} onChange={setDatePeriod} />
          }
        />
        <div className={classes.chartGrid}>
          <VisitsOverTimeCard
            data={visitsQuery.data}
            isLoading={visitsQuery.isFetching}
          />
          <OrdersOverTimeCard
            data={ordersQuery.data}
            isLoading={ordersQuery.isFetching}
          />
        </div>

        <div className={classes.tableGrid}>
          <ZeroResultSearchesCard
            data={zeroResultSearchesQuery.data}
            isLoading={zeroResultSearchesQuery.isFetching}
          />
          <PopularSearchesCard
            data={popularSearchesQuery.data}
            isLoading={popularSearchesQuery.isFetching}
          />
        </div>
      </div>
    </>
  );
}

export const Route = createFileRoute('/_main-ui/_protected/analytics/')({
  component: AnalyticsPage,
  loader: async () => {
    if (config('features.analytics.enabled') !== true) throw notFound();
  },
});
